/**
 * Connection + room state: the server's latest RoomView for this seat, the
 * connection status, the server clock offset and join/kick status.
 *
 * The server is authoritative; this store never invents game state. It only
 * filters out stale views (lower `version`) and derives a clock offset from
 * `serverNow` so countdowns are accurate regardless of local clock skew.
 */
import { create } from 'zustand';
import type { KickedPayload, RoomView, Seat, ServerHello } from '@wordloom/engine';
import type { ClientErrorPayload } from '../net/errors';

export type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting';

/** The room this tab is (trying to be) seated in. */
export interface RoomSession {
  code: string;
  seat: Seat | null;
  /** True once `room:join`/`room:create` was acked on the CURRENT socket connection. */
  joined: boolean;
  /**
   * True after `room:kicked {reason:'replaced'}`: another tab owns the seat, so
   * this tab must NOT auto-rejoin on reconnect (that would ping-pong the seat).
   */
  suspended: boolean;
}

/**
 * Version filtering: accept a view unless it is an OLDER version of the room
 * we already hold. (Equal versions are idempotent re-sends and are accepted.)
 */
export function shouldAcceptView(current: RoomView | null, incoming: RoomView): boolean {
  if (!current) return true;
  if (current.code !== incoming.code) return true;
  return incoming.version >= current.version;
}

/** serverNow − local receive time. Add it to Date.now() to get server time. */
export function computeClockOffset(serverNow: number, receivedAt: number): number {
  return serverNow - receivedAt;
}

export interface RoomStoreState {
  connection: ConnectionStatus;
  /** True once any connection succeeded (distinguishes "connecting…" from "reconnecting…"). */
  everConnected: boolean;
  hello: ServerHello | null;
  clockOffset: number;
  view: RoomView | null;
  session: RoomSession | null;
  joining: boolean;
  joinError: ClientErrorPayload | null;
  kicked: KickedPayload | null;

  setConnection(status: ConnectionStatus): void;
  receiveHello(hello: ServerHello, receivedAt: number): void;
  /** Returns false when the view was dropped as stale. */
  receiveView(view: RoomView, receivedAt: number): boolean;
  startSession(code: string): void;
  setJoining(joining: boolean): void;
  setJoined(code: string, seat: Seat): void;
  setJoinError(error: ClientErrorPayload | null): void;
  markDisconnected(): void;
  setKicked(payload: KickedPayload | null): void;
  clearRoom(): void;
}

export const useRoomStore = create<RoomStoreState>()((set, get) => ({
  connection: 'idle',
  everConnected: false,
  hello: null,
  clockOffset: 0,
  view: null,
  session: null,
  joining: false,
  joinError: null,
  kicked: null,

  setConnection(status) {
    set((s) => ({ connection: status, everConnected: s.everConnected || status === 'connected' }));
  },

  receiveHello(hello, receivedAt) {
    set({ hello, clockOffset: computeClockOffset(hello.serverNow, receivedAt) });
  },

  receiveView(view, receivedAt) {
    const { view: current, session, joining } = get();
    if (!shouldAcceptView(current, view)) return false;
    // A view for a room we are not in is ignored: another room's late push, or
    // any push after leaving (no session). Without a session only a room:create
    // in flight (`joining`) may take a view — its room:state can beat the ack.
    if (session ? session.code !== view.code : !joining) return false;
    set({ view, clockOffset: computeClockOffset(view.serverNow, receivedAt) });
    return true;
  },

  startSession(code) {
    const current = get().session;
    if (current?.code === code) {
      set({ session: { ...current, suspended: false }, joinError: null });
      return;
    }
    const view = get().view;
    set({
      session: { code, seat: null, joined: false, suspended: false },
      // Keep a view that already arrived for this room (room:state can beat the ack).
      view: view?.code === code ? view : null,
      joinError: null,
      kicked: null,
    });
  },

  setJoining(joining) {
    set({ joining });
  },

  setJoined(code, seat) {
    set((s) => ({
      session: { code, seat, joined: true, suspended: false },
      joining: false,
      joinError: null,
      kicked: s.kicked?.reason === 'replaced' ? null : s.kicked,
    }));
  },

  setJoinError(error) {
    set({ joinError: error, joining: false });
  },

  markDisconnected() {
    const session = get().session;
    if (session) set({ session: { ...session, joined: false } });
  },

  setKicked(payload) {
    const session = get().session;
    set({
      kicked: payload,
      session: session && payload ? { ...session, joined: false, suspended: true } : session,
    });
  },

  clearRoom() {
    set({ view: null, session: null, joining: false, joinError: null, kicked: null });
  },
}));

/** Server time now, using the latest clock offset. */
export function serverNow(): number {
  return Date.now() + useRoomStore.getState().clockOffset;
}
